import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { TRANSLATIONS } from '@/core/i18n/translations';

interface FitFtpModalProps {
  open: boolean;
  language: 'en' | 'pl';
  onCancel: () => void;
  /** Second argument is true when the user chose to persist FTP (profile / browser). */
  onConfirm: (ftpWatts: number, savePermanently: boolean) => void;
}

export function FitFtpModal({ open, language, onCancel, onConfirm }: FitFtpModalProps) {
  const t = TRANSLATIONS[language];
  const [draft, setDraft] = useState('');
  const [savePermanent, setSavePermanent] = useState(true);

  useEffect(() => {
    if (open) {
      setDraft('');
      setSavePermanent(true);
    }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const n = Number.parseInt(draft.trim(), 10);
    if (!Number.isFinite(n) || n < 30 || n > 750) {
      toast.error(t.fitFtpInvalid);
      return;
    }
    onConfirm(n, savePermanent);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        data-testid="fit-ftp-modal"
        className="font-sans bg-white dark:bg-slate-900 p-6 rounded-xl max-w-sm w-full max-h-[min(520px,90svh)] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800"
      >
        <div className="flex justify-between items-start gap-2 mb-3">
          <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white pr-6">{t.fitFtpModalTitle}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[44px] min-w-[44px] shrink-0 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 touch-manipulation"
            aria-label={t.close}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{t.fitFtpModalHint}</p>
        <input
          data-testid="fit-ftp-input"
          type="number"
          inputMode="numeric"
          min={30}
          max={750}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          className="w-full p-3 border rounded-lg dark:bg-slate-950 dark:border-slate-800 mb-3 focus:ring-2 focus:ring-orange-500 outline-none"
          placeholder={t.fitFtpPlaceholder}
          autoFocus
        />
        <label className="mb-4 flex items-start gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            data-testid="fit-ftp-save-permanent"
            checked={savePermanent}
            onChange={(e) => setSavePermanent(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-orange-700 focus:ring-orange-600"
          />
          <span>{t.fitFtpSavePermanentLabel}</span>
        </label>
        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            data-testid="fit-ftp-cancel"
            onClick={onCancel}
            className="min-h-[44px] px-5 py-2.5 text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors touch-manipulation"
          >
            {t.fitFtpCancel}
          </button>
          <button
            type="button"
            data-testid="fit-ftp-confirm"
            onClick={submit}
            className="min-h-[44px] bg-orange-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-orange-500 transition-all touch-manipulation"
          >
            {t.fitFtpConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}
